import { useUTM, useWindowOpen, useWorkingPlatform } from '@repo/platform';
import { useAuthContext } from '@repo/user-session';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';

import { adSlotQuery, adSlotQuery$data, PlacementType, UtmInput } from '@/__generated__/adSlotQuery.graphql';
import { adSlot } from '@/graphql/query/common/ad-slot';
import { HOME_BOTTOM_BENEFIT_LENGTH, SlotTargetType, SlotType } from '@/lib/constants/ad-slots';
import { adLabIdsAtom } from '@/lib/stores/ads';

export const useAdSlots = (type: SlotType, corpType: SlotTargetType) => {
  const relayEnv = useRelayEnvironment();
  const { auth } = useAuthContext();
  const { workingPlatform } = useWorkingPlatform();
  const { utmData } = useUTM();
  const router = useRouter();
  const { openWindow } = useWindowOpen();

  const [slotItems, setSlotItems] = useState<adSlotQuery$data['getAdSlot'][]>([]);

  const { labItemIdx } = useAdSlotLabSettings(slotItems[0] as adSlotQuery$data['getAdSlot']);

  useEffect(() => {
    if (!auth.apiToken) return;

    const fetchAdSlotData = async (type: PlacementType, target: string) => {
      try {
        const res = await fetchQuery<adSlotQuery>(relayEnv, adSlot, {
          placementType: type,
          target,
          workingPlatform,
          utm: {
            term: utmData?.utmTerm,
            campaign: utmData?.utmCampaign,
            content: utmData?.utmContent,
            medium: utmData?.utmMedium,
            source: utmData?.utmSource
          } as UtmInput | undefined
        }).toPromise();
        if (res?.getAdSlot?.result) {
          return res?.getAdSlot;
        }
        return undefined;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    };

    if (type === 'HOME_BOTTOM_BENEFIT') {
      const fetchSequentially = async () => {
        const results: adSlotQuery$data['getAdSlot'][] = [];
        for (let i = 0; i < HOME_BOTTOM_BENEFIT_LENGTH; i++) {
          const result = await fetchAdSlotData(`${type}_${i + 1}` as PlacementType, corpType);
          if (result?.items?.length) {
            results.push(result);
          }
        }
        if (results.length > 0) {
          setSlotItems(results);
        }
      };
      fetchSequentially();
    } else {
      fetchAdSlotData(type as PlacementType, corpType).then((result) => {
        if (result?.items?.length) {
          setSlotItems([result]);
        }
      });
    }
  }, [auth.apiToken, relayEnv, type, workingPlatform, utmData, corpType]);

  const handleRouteChange = (linkUrl: string) => {
    const isSameOrigin = new URL(linkUrl ?? '').origin === window.location.origin;
    if (isSameOrigin) {
      router.push(linkUrl ?? '');
    } else {
      openWindow(linkUrl ?? '');
    }
  };

  const replaceNewLine = (text: string | undefined | null) => text?.replace(/\\n/g, '\n') ?? '';

  return { slots: slotItems, labItemIdx, handleRouteChange, replaceNewLine };
};

const useAdSlotLabSettings = (slotData: adSlotQuery$data['getAdSlot']) => {
  const [labId, setLabId] = useState<string>();
  const [adLabIds, setAdLabIds] = useAtom(adLabIdsAtom);
  const [isHydrated, setIsHydrated] = useState(false);

  // localStorage hydration 완료 체크
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const slotItems = useMemo(() => (isHydrated && slotData?.result ? slotData.items : []), [isHydrated, slotData]);
  const placementType = slotData?.placementType;

  useEffect(() => {
    if (!slotItems || !isHydrated || !placementType) return;

    let savedLabId: string | null = adLabIds?.[placementType] || null;
    // 이미 저장된 키가 이번 실험 대상이 아닐때
    if (savedLabId && slotItems.findIndex(({ displayCode }) => `${displayCode}` === savedLabId) === -1) {
      savedLabId = null;
      setAdLabIds((prev) => ({ ...prev, [placementType]: '' }) as Record<PlacementType, string>);
    }

    if (savedLabId) {
      setLabId(savedLabId);
    } else {
      // ratio 기반 가중치 선택 로직 (ratio 합계는 항상 100)
      const randomValue = Math.random() * 100;
      let accumulatedRatio = 0;

      for (const item of slotItems) {
        accumulatedRatio += item.displayRatio || 0;
        if (randomValue <= accumulatedRatio) {
          const newLabId = `${item.displayCode}`;
          setLabId(newLabId);
          setAdLabIds((prev) => ({ ...prev, [placementType]: newLabId }) as Record<PlacementType, string>);
          break;
        }
      }
    }
  }, [placementType, slotItems, adLabIds, setAdLabIds, isHydrated]);

  const itemIdx = slotItems?.findIndex(({ displayCode }) => displayCode === labId) ?? -1;

  return {
    labItemIdx: isHydrated && slotItems?.length === 0 ? -1 : itemIdx
  };
};
